import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/Button.js";
import { DataTable } from "@primereact/ui/datatable";
import { Dialog } from "@primereact/ui/dialog";
import { InputText as Input } from "@primereact/ui/inputtext";
import { Paginator } from "@primereact/ui/paginator";
import { Select } from "@primereact/ui/select";
import { Tabs } from "@primereact/ui/tabs";
import { PageHeader } from "../../components/ui/PageHeader.js";
import { Field } from "../../components/ui/Field.js";
import { apiGet, apiPost } from "../../lib/api.js";
import { usePermissions } from "../../lib/auth.js";
import { useNavigate } from "react-router-dom";
import {
  notificationHref,
  notificationText,
  type NotificationRecord,
} from "../../lib/notifications.js";
import { ErrorPanel, LoadingPanel, formatDate } from "../shared.js";

type Notification = NotificationRecord & {
  id: string;
  readAt?: string;
  createdAt: string;
};
type NotificationPage = { data: Notification[]; meta: { unread: number } };
type AuditEntry = {
  id: string;
  event: string;
  summary: string;
  entityType: string | null;
  entityId: string | null;
  actorId: string | null;
  actor: string;
  metadata: unknown;
  createdAt: string;
};
type AuditPage = {
  data: AuditEntry[];
  page: number;
  pageSize: number;
  total: number;
  facets: {
    events: Array<{ value: string; count: number }>;
    entityTypes: Array<{ value: string; count: number }>;
  };
};
type ActorOption = { id: string; label: string };

function NotificationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const canViewAudit = permissions.has("settings.system.edit");
  const [tab, setTab] = useState("notifications");
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(25);
  const [auditSearch, setAuditSearch] = useState("");
  const [auditEvent, setAuditEvent] = useState("");
  const [auditEntityType, setAuditEntityType] = useState("");
  const [auditActorId, setAuditActorId] = useState("");
  const [auditActorSearch, setAuditActorSearch] = useState("");
  const [auditFrom, setAuditFrom] = useState("");
  const [auditTo, setAuditTo] = useState("");
  const [auditSortField, setAuditSortField] = useState<
    "createdAt" | "event" | "summary" | "entityType" | "actor"
  >("createdAt");
  const [auditSortOrder, setAuditSortOrder] = useState<1 | -1>(-1);
  const [metadataEntry, setMetadataEntry] = useState<AuditEntry | null>(null);
  const query = useQuery({
    queryKey: ["notifications"],
    queryFn: () =>
      apiGet<NotificationPage>("/notifications", { query: { pageSize: 100 } }),
  });
  const audit = useQuery({
    queryKey: [
      "notifications",
      "audit",
      auditPage,
      auditPageSize,
      auditSearch,
      auditEvent,
      auditEntityType,
      auditActorId,
      auditFrom,
      auditTo,
      auditSortField,
      auditSortOrder,
    ],
    queryFn: () =>
      apiGet<AuditPage>("/notifications/audit", {
        query: {
          page: auditPage,
          pageSize: auditPageSize,
          q: auditSearch || undefined,
          event: auditEvent || undefined,
          entityType: auditEntityType || undefined,
          actorId: auditActorId || undefined,
          from: auditFrom || undefined,
          to: auditTo || undefined,
          sortField: auditSortField,
          sortOrder: auditSortOrder,
        },
      }),
    enabled: tab === "audit" && canViewAudit,
  });
  const auditActors = useQuery({
    queryKey: ["notifications", "audit", "actors", auditActorSearch],
    queryFn: () =>
      apiGet<{ data: ActorOption[] }>("/notifications/audit/actors", {
        query: { q: auditActorSearch || undefined },
      }).then((response) => response.data),
    enabled: tab === "audit" && canViewAudit,
  });
  const mark = useMutation({
    mutationFn: (id?: string) =>
      apiPost(id ? `/notifications/${id}/read` : "/notifications/read-all"),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
  const changeAuditSort = (
    field: "createdAt" | "event" | "summary" | "entityType" | "actor",
  ) => {
    setAuditPage(1);
    if (auditSortField !== field) {
      setAuditSortField(field);
      setAuditSortOrder(1);
    } else if (auditSortOrder === 1) {
      setAuditSortOrder(-1);
    } else {
      setAuditSortField("createdAt");
      setAuditSortOrder(-1);
    }
  };
  const sortIndicator = (field: typeof auditSortField) =>
    auditSortField === field ? (auditSortOrder === 1 ? " ↑" : " ↓") : "";
  return (
    <>
      <PageHeader
        title={t("notifications.title", { defaultValue: "Notifications" })}
        subtitle={t("notifications.subtitle", {
          defaultValue: "Updates about requests, offers and invoices.",
        })}
        actions={
          query.data?.meta.unread ? (
            <Button
              variant="outlined"
              severity="secondary"
              onClick={() => mark.mutate(undefined)}
              loading={mark.isPending || undefined}
            >
              {t("notifications.readAll", { defaultValue: "Mark all as read" })}
            </Button>
          ) : undefined
        }
      />
      <Tabs.Root
        value={tab}
        onValueChange={(event: { value: unknown }) =>
          setTab(String(event.value))
        }
      >
        <Tabs.List>
          <Tabs.Tab value="notifications">
            {t("notifications.title", { defaultValue: "Notifications" })}
          </Tabs.Tab>
          {canViewAudit ? (
            <Tabs.Tab value="audit">
              {t("notifications.audit", { defaultValue: "Audit log" })}
            </Tabs.Tab>
          ) : null}
        </Tabs.List>
        <Tabs.Panels>
          <Tabs.Panel value="notifications">
            {query.isLoading ? (
              <LoadingPanel />
            ) : query.error ? (
              <ErrorPanel error={query.error} />
            ) : (
              <div>
                {query.data?.data.length ? (
                  <ol className="divide-y divide-app-border">
                    {query.data.data.map((notification) => {
                      const text = notificationText(notification, t);
                      const href = notificationHref(notification);
                      return (
                        <li
                          key={notification.id}
                          className={`py-4 ${notification.readAt ? "opacity-70" : ""}`}
                        >
                          <button
                            type="button"
                            className="w-full text-left"
                            onClick={() => {
                              if (!notification.readAt)
                                mark.mutate(notification.id);
                              if (href) navigate(href);
                            }}
                          >
                            <span className="flex items-start justify-between gap-4">
                              <strong>{text.title}</strong>
                              <time className="whitespace-nowrap text-xs text-app-muted">
                                {formatDate(notification.createdAt)}
                              </time>
                            </span>
                            {text.body ? (
                              <span className="mt-1 block text-sm text-app-muted">
                                {text.body}
                              </span>
                            ) : null}
                            {!notification.readAt ? (
                              <span className="mt-2 block text-xs font-medium text-brand">
                                {t("notifications.unread", {
                                  defaultValue: "Unread",
                                })}
                              </span>
                            ) : null}
                          </button>
                        </li>
                      );
                    })}
                  </ol>
                ) : (
                  <p className="text-sm text-app-muted">
                    {t("notifications.empty", {
                      defaultValue: "No notifications.",
                    })}
                  </p>
                )}
              </div>
            )}
          </Tabs.Panel>
          <Tabs.Panel value="audit">
            <div className="mb-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <Field
                label={t("common.search", { defaultValue: "Search" })}
                className="xl:col-span-2"
              >
                <Input
                  type="search"
                  value={auditSearch}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setAuditSearch(event.target.value);
                    setAuditPage(1);
                  }}
                />
              </Field>
              <Field
                label={t("notifications.event", { defaultValue: "Event" })}
              >
                <Select.Root
                  fluid
                  value={auditEvent}
                  options={[
                    {
                      value: "",
                      label: t("common.all", { defaultValue: "All" }),
                    },
                    ...(audit.data?.facets.events ?? []).map((facet) => ({
                      value: facet.value,
                      label: `${facet.value} (${facet.count})`,
                    })),
                  ]}
                  optionLabel="label"
                  optionValue="value"
                  onValueChange={(event: { value: unknown }) => {
                    setAuditEvent(String(event.value ?? ""));
                    setAuditPage(1);
                  }}
                >
                  <Select.Trigger type="button">
                    <Select.Value />
                    <Select.Indicator className="pi pi-chevron-down" />
                  </Select.Trigger>
                  <Select.Portal>
                    <Select.Positioner>
                      <Select.Popup>
                        <Select.List />
                      </Select.Popup>
                    </Select.Positioner>
                  </Select.Portal>
                </Select.Root>
              </Field>
              <Field
                label={t("notifications.entityType", {
                  defaultValue: "Entity type",
                })}
              >
                <Select.Root
                  fluid
                  value={auditEntityType}
                  options={[
                    {
                      value: "",
                      label: t("common.all", { defaultValue: "All" }),
                    },
                    ...(audit.data?.facets.entityTypes ?? []).map((facet) => ({
                      value: facet.value,
                      label: `${facet.value} (${facet.count})`,
                    })),
                  ]}
                  optionLabel="label"
                  optionValue="value"
                  onValueChange={(event: { value: unknown }) => {
                    setAuditEntityType(String(event.value ?? ""));
                    setAuditPage(1);
                  }}
                >
                  <Select.Trigger type="button">
                    <Select.Value />
                    <Select.Indicator className="pi pi-chevron-down" />
                  </Select.Trigger>
                  <Select.Portal>
                    <Select.Positioner>
                      <Select.Popup>
                        <Select.List />
                      </Select.Popup>
                    </Select.Positioner>
                  </Select.Portal>
                </Select.Root>
              </Field>
              <Field
                label={t("notifications.actorSearch", {
                  defaultValue: "Find actor",
                })}
              >
                <Input
                  type="search"
                  value={auditActorSearch}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setAuditActorSearch(event.target.value)
                  }
                />
              </Field>
              <Field
                label={t("notifications.actor", { defaultValue: "Actor" })}
              >
                <Select.Root
                  fluid
                  value={auditActorId}
                  options={[
                    { id: "", label: t("common.all", { defaultValue: "All" }) },
                    ...(auditActors.data ?? []),
                  ]}
                  optionLabel="label"
                  optionValue="id"
                  onValueChange={(event: { value: unknown }) => {
                    setAuditActorId(String(event.value ?? ""));
                    setAuditPage(1);
                  }}
                >
                  <Select.Trigger type="button">
                    <Select.Value />
                    <Select.Indicator className="pi pi-chevron-down" />
                  </Select.Trigger>
                  <Select.Portal>
                    <Select.Positioner>
                      <Select.Popup>
                        <Select.List />
                      </Select.Popup>
                    </Select.Positioner>
                  </Select.Portal>
                </Select.Root>
              </Field>
              <Field label={t("common.from", { defaultValue: "From" })}>
                <Input
                  type="date"
                  value={auditFrom}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setAuditFrom(event.target.value);
                    setAuditPage(1);
                  }}
                />
              </Field>
              <Field label={t("common.to", { defaultValue: "To" })}>
                <Input
                  type="date"
                  value={auditTo}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setAuditTo(event.target.value);
                    setAuditPage(1);
                  }}
                />
              </Field>
            </div>
            {audit.isLoading ? (
              <LoadingPanel />
            ) : audit.error ? (
              <ErrorPanel error={audit.error} />
            ) : (
              <>
                <DataTable.Root data={audit.data?.data ?? []} dataKey="id">
                  <DataTable.TableContainer>
                    <DataTable.Table>
                      <DataTable.THead>
                        <DataTable.Row>
                          {(
                            [
                              [
                                "createdAt",
                                t("notifications.created", {
                                  defaultValue: "Created",
                                }),
                              ],
                              [
                                "event",
                                t("notifications.event", {
                                  defaultValue: "Event",
                                }),
                              ],
                              [
                                "summary",
                                t("notifications.summary", {
                                  defaultValue: "Summary",
                                }),
                              ],
                              [
                                "actor",
                                t("notifications.actor", {
                                  defaultValue: "Actor",
                                }),
                              ],
                              [
                                "entityType",
                                t("notifications.entityType", {
                                  defaultValue: "Entity type",
                                }),
                              ],
                            ] as const
                          ).map(([field, label]) => (
                            <DataTable.THeadCell key={field}>
                              <button
                                type="button"
                                className="font-semibold"
                                onClick={() => changeAuditSort(field)}
                              >
                                {label}
                                {sortIndicator(field)}
                              </button>
                            </DataTable.THeadCell>
                          ))}
                          <DataTable.THeadCell>
                            <DataTable.THeadTitle>
                              {t("notifications.entityId", {
                                defaultValue: "Entity ID",
                              })}
                            </DataTable.THeadTitle>
                          </DataTable.THeadCell>
                        </DataTable.Row>
                      </DataTable.THead>
                      <DataTable.TBody className="divide-y divide-app-border">
                        {(audit.data?.data ?? []).map((entry) => (
                          <DataTable.Row key={entry.id}>
                            <DataTable.Cell className="whitespace-nowrap">
                              {formatDate(entry.createdAt)}
                            </DataTable.Cell>
                            <DataTable.Cell>{entry.event}</DataTable.Cell>
                            <DataTable.Cell>
                              {entry.metadata ? (
                                <button
                                  type="button"
                                  className="text-left font-medium text-brand hover:underline"
                                  onClick={() => setMetadataEntry(entry)}
                                >
                                  {entry.summary}
                                </button>
                              ) : (
                                entry.summary
                              )}
                            </DataTable.Cell>
                            <DataTable.Cell>{entry.actor}</DataTable.Cell>
                            <DataTable.Cell>
                              {entry.entityType ?? "—"}
                            </DataTable.Cell>
                            <DataTable.Cell className="font-mono text-xs">
                              {entry.entityId ?? "—"}
                            </DataTable.Cell>
                          </DataTable.Row>
                        ))}
                        {!audit.data?.data.length ? (
                          <DataTable.Row>
                            <DataTable.Cell
                              colSpan={6}
                              className="py-10 text-center text-app-muted"
                            >
                              {t("notifications.noAudit", {
                                defaultValue: "No audit events.",
                              })}
                            </DataTable.Cell>
                          </DataTable.Row>
                        ) : null}
                      </DataTable.TBody>
                    </DataTable.Table>
                  </DataTable.TableContainer>
                </DataTable.Root>
                <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <Select.Root
                    value={auditPageSize}
                    options={[10, 25, 50, 100].map((value) => ({
                      value,
                      label: t("notifications.rows", {
                        defaultValue: "{{count}} rows",
                        count: value,
                      }),
                    }))}
                    optionLabel="label"
                    optionValue="value"
                    onValueChange={(event: { value: unknown }) => {
                      setAuditPageSize(Number(event.value));
                      setAuditPage(1);
                    }}
                  >
                    <Select.Trigger type="button">
                      <Select.Value />
                      <Select.Indicator className="pi pi-chevron-down" />
                    </Select.Trigger>
                    <Select.Portal>
                      <Select.Positioner>
                        <Select.Popup>
                          <Select.List />
                        </Select.Popup>
                      </Select.Positioner>
                    </Select.Portal>
                  </Select.Root>
                  <Paginator.Root
                    page={auditPage}
                    total={audit.data?.total ?? 0}
                    itemsPerPage={auditPageSize}
                    onPageChange={(event: { value: number }) =>
                      setAuditPage(event.value)
                    }
                  />
                </div>
              </>
            )}
          </Tabs.Panel>
        </Tabs.Panels>
      </Tabs.Root>
      <Dialog.Root
        open={metadataEntry !== null}
        modal
        onOpenChange={(event: { value: boolean | undefined }) => {
          if (!event.value) setMetadataEntry(null);
        }}
      >
        <Dialog.Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Popup>
              <Dialog.Header>
                <Dialog.Title>
                  {metadataEntry?.summary ??
                    t("notifications.metadata", { defaultValue: "Metadata" })}
                </Dialog.Title>
                <Dialog.HeaderActions>
                  <Dialog.Close className="pi pi-times" />
                </Dialog.HeaderActions>
              </Dialog.Header>
              <Dialog.Content>
                <pre className="max-h-[60vh] overflow-auto rounded-lg bg-app-background p-4 text-xs">
                  {JSON.stringify(metadataEntry?.metadata, null, 2)}
                </pre>
              </Dialog.Content>
            </Dialog.Popup>
          </Dialog.Positioner>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

export const dashboardRoutes = [
  { path: "notifications", element: <NotificationsPage /> },
];
